import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { useSelector } from "react-redux";
import type { BulkJob } from "../Types/accountQuery";
import { getUserFromLS } from "../utils/getUserFromLS";
import { AccountEditResults } from "../Components/Account/AccountEditResults";
import { AccountSearchDialog } from "../Components/Account/AccountSearchDialog";
import { useObservedJob } from "../hooks/useObservedJob";
import { readRegisteredJob } from "../Services/JobRegistry";
import type { JobReference } from "../Services/JobRefreshCoordinator";

type Context = {
  notice?: BulkJob;
  closeNotice: () => void;
  accept: (job: BulkJob) => void;
  open: (job: BulkJob) => void;
};
const AccountEditContext = createContext<Context>({ closeNotice: () => {}, accept: () => {}, open: () => {} });
export const useAccountEditNotifications = () => useContext(AccountEditContext);

/** Submission feedback and an explicit progress dialog. Alerts live in Firestore. */
export function AccountEditNotificationProvider({ children }: { children: ReactNode }) {
  const auth = useSelector((state: { auth: { isAuthenticated: boolean; user?: { id?: string } } }) => state.auth);
  const userId = auth.isAuthenticated ? auth.user?.id || getUserFromLS()?.id : undefined;
  return <OwnedFeedback key={userId ?? "signed-out"} userId={userId}>{children}</OwnedFeedback>;
}

function OwnedFeedback({ userId, children }: { userId?: string; children: ReactNode }) {
  const [notice, setNotice] = useState<BulkJob>();
  const [opened, setOpened] = useState<{ id: string; clientId: string; initial?: BulkJob }>();
  const closeNotice = useCallback(() => setNotice(undefined), []);
  const accept = useCallback((job: BulkJob) => setNotice(job), []);
  const open = useCallback((job: BulkJob) => setOpened({ id: job.id, clientId: job.client_id, initial: job }), []);
  useEffect(() => {
    if (!userId || window.location.pathname !== "/all-accounts") return;
    const params = new URLSearchParams(window.location.search);
    const id = params.get("jobId"), clientId = params.get("clientId");
    if (id && clientId && /^[a-zA-Z0-9_.:-]{1,128}$/.test(id)) setOpened({ id, clientId });
  }, [userId]);
  return <AccountEditContext.Provider value={{ notice, closeNotice, accept, open }}>
    {children}
    {opened && userId && <ObservedResults key={opened.id} {...opened} userId={userId} onClose={() => setOpened(undefined)} />}
  </AccountEditContext.Provider>;
}

function ObservedResults({ id, clientId, userId, initial, onClose }: {
  id: string; clientId: string; userId: string; initial?: BulkJob; onClose: () => void;
}) {
  const job: JobReference = initial?.job_reference ?? { owner: "database_service", kind: "account_bulk", id, attempt: "1" };
  const request = useObservedJob(job, signal => readRegisteredJob("account_bulk", job, { clientId, actorId: userId }, signal), false);
  const denied = !!request.error && typeof request.error === "object" && "status" in request.error && [401, 403, 404].includes(Number(request.error.status));
  const value = request.currentData ?? (denied ? undefined : initial);
  if (!value) return <AccountSearchDialog title="Account edit results" onClose={onClose}>
    <p role="status">{request.error ? "This job is unavailable. Refresh to check your access." : "Loading account edit results…"}</p>
    <button type="button" onClick={request.refetch}>Refresh</button>
    <button type="button" onClick={onClose}>Close</button>
  </AccountSearchDialog>;
  return <AccountEditResults job={value} onClose={onClose} delayed={request.delayed} refresh={request.refetch} />;
}
